import BannerNotification from '@atoms/BannerNotification/BannerNotification';
import BannerNotificationStylesEnum from '@models/enums/BannerNotificationStylesEnum';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';

interface Props {
  readonly accountDefaultAddressUpdateLabel: string;
  readonly accountDeleteAddressLabel: string;
  readonly accountDeleteContactSubmitLabel: string;
  readonly accountExtendedWarrantyRegistrationErrorLabel: string;
  readonly accountExtendedWarrantyStepSubmitLabel: string;
  readonly accountExtendedWarrantyProductSearchLabel: string;
  readonly accountExtendedWarrantyProofOfPurchaseUploadErrorLabel: string;
  readonly accountExtendedWarrantyProofOfPurchaseDownloadErrorLabel: string;
  readonly accountGetOrderDetailsLabel: string;
  readonly apiDownLabel: string;
  readonly cartRemoveItemLabel: string;
  readonly closeLabel: string;
  readonly lightboxOuterImagesLabel: string;
  readonly miniBasketDetailsLabel: string;
  readonly newsletterSignupBannerLabel: string;
  readonly newsletterSignupPopupLabel: string;
  readonly onlineRetailersDataLabel: string;
  readonly orderSummaryRemoveVoucherLabel: string;
  readonly productDetailsFetchLabel: string;
  readonly productDetailsNotifyMeLabel: string;
  readonly searchResultsLabel: string;
  readonly shippingPickUpLocationsLabel: string;
}

const ErrorBannerNotification = ({
  accountDefaultAddressUpdateLabel,
  accountDeleteAddressLabel,
  accountDeleteContactSubmitLabel,
  accountExtendedWarrantyStepSubmitLabel,
  accountExtendedWarrantyProductSearchLabel,
  accountExtendedWarrantyRegistrationErrorLabel,
  accountExtendedWarrantyProofOfPurchaseUploadErrorLabel,
  accountExtendedWarrantyProofOfPurchaseDownloadErrorLabel,
  accountGetOrderDetailsLabel,
  apiDownLabel,
  cartRemoveItemLabel,
  closeLabel,
  lightboxOuterImagesLabel,
  miniBasketDetailsLabel,
  newsletterSignupBannerLabel,
  newsletterSignupPopupLabel,
  onlineRetailersDataLabel,
  orderSummaryRemoveVoucherLabel,
  productDetailsFetchLabel,
  productDetailsNotifyMeLabel,
  searchResultsLabel,
  shippingPickUpLocationsLabel,
}: Props) => {
  const bannerRef = useRef<HTMLDivElement>(null);

  const { errorBannerNotification, setErrorBannerNotification } =
    useErrorNotificationStore();

  const ErrorBannerNotificationLookup = Object.fromEntries(
    Object.entries(ErrorBannerNotificationEnum).map(([key, value]) => [
      value,
      {
        AccountDefaultAddressUpdate: accountDefaultAddressUpdateLabel,
        AccountDeleteAddress: accountDeleteAddressLabel,
        AccountDeleteContactSubmit: accountDeleteContactSubmitLabel,
        AccountExtendedWarrantyStepSubmit:
          accountExtendedWarrantyStepSubmitLabel,
        AccountExtendedWarrantyProductSearch:
          accountExtendedWarrantyProductSearchLabel,
        AccountExtendedWarrantyRegistrationError:
          accountExtendedWarrantyRegistrationErrorLabel,
        AccountGetOrderDetails: accountGetOrderDetailsLabel,
        AccountExtendedWarrantyProofOfPurchaseUploadError:
          accountExtendedWarrantyProofOfPurchaseUploadErrorLabel,
        AccountExtendedWarrantyProofOfPurchaseDownloadError:
          accountExtendedWarrantyProofOfPurchaseDownloadErrorLabel,
        CartRemoveItem: cartRemoveItemLabel,
        LightboxOuterImages: lightboxOuterImagesLabel,
        MiniBasketDetails: miniBasketDetailsLabel,
        NewsletterSignupBanner: newsletterSignupBannerLabel,
        NewsletterSignupPopup: newsletterSignupPopupLabel,
        OnlineRetailersData: onlineRetailersDataLabel,
        OrderSummaryRemoveVoucher: orderSummaryRemoveVoucherLabel,
        ProductDetailsFetch: productDetailsFetchLabel,
        ProductDetailsNotifyMe: productDetailsNotifyMeLabel,
        SearchResults: searchResultsLabel,
        ShippingPickUpLocations: shippingPickUpLocationsLabel,
      }[key as keyof typeof ErrorBannerNotificationEnum],
    ])
  ) as Record<ErrorBannerNotificationEnum, string>;

  const currentError = errorBannerNotification
    ? ErrorBannerNotificationLookup[errorBannerNotification]
    : '';

  const handleDismiss = () => setErrorBannerNotification(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional missing dependencies
  useEffect(() => {
    const handleClickOutsideNotification = ({ target }: Event) => {
      if (
        errorBannerNotification &&
        !bannerRef.current?.contains(target as HTMLElement)
      ) {
        handleDismiss();
      }
    };

    window.addEventListener(
      'pointerdown',
      handleClickOutsideNotification,
      true
    );

    return () => {
      window.removeEventListener(
        'pointerdown',
        handleClickOutsideNotification,
        true
      );
    };
  }, [errorBannerNotification]);

  return (
    <AnimatePresence>
      {errorBannerNotification && (
        <motion.div
          ref={bannerRef}
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <BannerNotification
            displayStyle={BannerNotificationStylesEnum.Error}
            iconName="warning"
            caption={apiDownLabel}
            title={currentError}
            closeLabel={closeLabel}
            className="bg-white max-md:container"
            onDismissNotification={handleDismiss}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ErrorBannerNotification;
